import { MDXRemoteSerializeResult } from "next-mdx-remote";
import { serialize } from "next-mdx-remote/serialize";
import remarkGfm from "remark-gfm";

export function getStrapiURL(path = "") {
  // ONE env var (owner, 2026-09-07 — CMS_BASE_URL was discarded):
  // NEXT_PUBLIC_STRAPI_API_URL, the name the generated layer and
  // sweetandbitter both use. NEXT_PUBLIC_ because markdown image rewriting
  // needs it in the client bundle too — it is a public URL, nothing secret.
  //
  // No localhost:1337 default. A wrong-but-plausible fallback turns a missing
  // env var into "fetch failed" against a port nothing listens on, which is
  // exactly how this cost an afternoon — better to say what is wrong.
  const base = process.env.NEXT_PUBLIC_STRAPI_API_URL;
  if (!base) {
    throw new Error(
      "NEXT_PUBLIC_STRAPI_API_URL is not set. The web app cannot reach the " +
        "CMS without it (check .env.development / .env.production)."
    );
  }
  return `${base}${path}`;
}

export const getStrapiFileUrl = (relativeUrl: string) => {
  if (!relativeUrl || relativeUrl == "") {
    return;
  }

  return getStrapiURL(relativeUrl);
};

export function getStrapiMedia(url: string | null) {
  if (url == null) {
    return null;
  }

  // Return the full URL if the media is hosted on an external provider
  if (url.startsWith("http") || url.startsWith("//")) {
    return url;
  }

  // Otherwise prepend the URL path with the Strapi URL
  return `${getStrapiURL()}${url}`;
}

export function formatDate(dateString: string) {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  };
  return date.toLocaleDateString("en-US", options);
}

/**
 * Markdown → plain text for structured data (FAQPage acceptedAnswer.text
 * etc.). Keeps link LABELS, drops URLs/images/formatting. JSON-LD must never
 * claim markup the page doesn't show — and must never ship markdown syntax.
 */
export const markdownToPlainText = (content: string): string =>
  (content ?? "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "") // images: drop entirely
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1") // links: keep the label
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_`~]+/g, "")
    .replace(/^\s*[-+>]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();

export const parseStrapiContent = async (
  content: string
): Promise<MDXRemoteSerializeResult | undefined> => {
  if (!content || content == "") {
    return null;
  }
  content = content || "";
  // strip raw HTML comments (MDX cannot parse `<!-- -->`; seeded/legacy
  // markdown may carry them, e.g. placeholder TODO notes)
  content = content.replace(/<!--[\s\S]*?-->/g, "");
  if (content.trim() === "") {
    return null;
  }
  const regex = /\]\(\/uploads\//gi;
  const proccesedContent = content.replace(
    regex,
    `](${process.env.NEXT_PUBLIC_STRAPI_API_URL}/uploads/`
  );

  // TODO: Set labels for footnotes in current locale
  // https://github.com/remarkjs/remark-rehype

  return await serialize(proccesedContent, {
    mdxOptions: {
      remarkPlugins: [remarkGfm],
      rehypePlugins: [],
      format: "mdx",
    },
  });
};

export const removeEmptyAttributes = <T>(obj: T): T => {
  if (typeof obj === "string" || obj instanceof String) {
    return obj;
  }

  if (Object.prototype.toString.call(obj) === "[object Array]") {
    return (obj as unknown[]).map((o) => removeEmptyAttributes(o)) as T;
  }

  const newObj = {};
  Object.keys(obj).forEach((key) => {
    if (Object.prototype.toString.call(obj[key]) === "[object Array]")
      newObj[key] = (obj[key] as unknown[]).map((o) => removeEmptyAttributes(o));
    else if (obj[key] === Object(obj[key]))
      newObj[key] = removeEmptyAttributes(obj[key]);
    else if (obj[key] !== undefined) newObj[key] = obj[key];
  });
  return newObj as T;
};

// ADDS DELAY TO SIMULATE SLOW API REMOVE FOR PRODUCTION
export const delay = (time: number) =>
  new Promise((resolve) => setTimeout(() => resolve(1), time));
